import type { Variants } from 'motion/react';
import * as motion from 'motion/react-m';
import { type SVGProps, useId } from 'react';
import { MotionProvider } from './motion';

// All chars in render order (S=0, then text left-to-right)
// The E group uses evenodd fill to punch the glyph through the box border
const CHARS = [
	{
		key: 'o1',
		d: 'M68.204 18.818c0 2.194-.41 4.074-1.232 5.638-.816 1.558-1.93 2.75-3.341 3.58-1.406.828-3 1.242-4.783 1.242-1.783 0-3.38-.414-4.793-1.242-1.405-.836-2.519-2.032-3.34-3.59-.816-1.564-1.224-3.44-1.224-5.628 0-2.194.408-4.07 1.224-5.628.821-1.564 1.935-2.76 3.34-3.59 1.412-.828 3.01-1.242 4.793-1.242 1.783 0 3.377.414 4.783 1.243 1.412.829 2.525 2.025 3.34 3.59.823 1.557 1.233 3.433 1.233 5.627Zm-3.708 0c0-1.544-.242-2.847-.726-3.908-.477-1.067-1.14-1.872-1.989-2.416-.848-.55-1.826-.825-2.933-.825-1.107 0-2.085.275-2.933.825-.849.544-1.515 1.35-1.999 2.417-.477 1.06-.716 2.363-.716 3.907 0 1.545.239 2.85.716 3.918.484 1.06 1.15 1.866 1.999 2.416.848.543 1.826.815 2.933.815 1.107 0 2.085-.271 2.933-.815.849-.55 1.512-1.356 1.989-2.416.484-1.067.726-2.373.726-3.918Z',
	},
	{
		key: 'h',
		d: 'M85.972 29V8.636h3.689v8.621h9.436v-8.62h3.699V29h-3.699v-8.65h-9.436V29h-3.689Z',
	},
	{
		key: 'a',
		d: 'M122.131 29h-3.937l7.169-20.364h4.554L137.086 29h-3.938l-5.439-16.188h-.159L122.131 29Zm.129-7.984h10.739v2.963H122.26v-2.963Z',
	},
	{
		key: 'n1',
		d: 'M172.235 29h-3.281l-9.595-13.87h-.169V29h-3.689V8.636h3.301l9.585 13.881h.179V8.637h3.669V29Z',
	},
	{
		// Box outer rect + E glyph as a single compound path with evenodd fill
		// Outer box: M186.402 0v35.47h40.277V0h-40.277Z (CW winding)
		// E glyph:   M197.59 7.882... (CCW winding inside box = punches through)
		key: 'e',
		d: 'M186.402 0v35.47h40.277V0h-40.277ZM197.59 7.882h17.901v3.941h-13.426v3.941h11.189v3.942h-11.189v3.94h13.426v3.942H197.59V7.882Z',
		fillRule: 'evenodd' as const,
	},
	{
		key: 'm',
		d: 'M240.757 8.325h4.514l6.046 14.756h.238l6.046-14.756h4.514V28.69h-3.54V14.7h-.189l-5.628 13.93h-2.644l-5.628-13.96h-.189v14.02h-3.54V8.325Z',
	},
	{
		key: 'o2',
		d: 'M298.608 18.507c0 2.194-.411 4.074-1.233 5.638-.815 1.558-1.929 2.751-3.341 3.58-1.405.828-2.999 1.242-4.782 1.242-1.784 0-3.381-.414-4.793-1.242-1.405-.836-2.519-2.032-3.341-3.59-.815-1.564-1.223-3.44-1.223-5.628 0-2.194.408-4.07 1.223-5.628.822-1.564 1.936-2.76 3.341-3.59 1.412-.828 3.009-1.242 4.793-1.242 1.783 0 3.377.414 4.782 1.243 1.412.829 2.526 2.025 3.341 3.59.822 1.557 1.233 3.433 1.233 5.627Zm-3.709 0c0-1.544-.242-2.847-.726-3.907-.477-1.068-1.14-1.873-1.988-2.417-.849-.55-1.826-.825-2.933-.825-1.107 0-2.085.275-2.934.825-.848.544-1.514 1.35-1.998 2.417-.478 1.06-.716 2.363-.716 3.907 0 1.545.238 2.85.716 3.918.484 1.06 1.15 1.866 1.998 2.416.849.544 1.827.815 2.934.815 1.107 0 2.084-.271 2.933-.815.848-.55 1.511-1.356 1.988-2.416.484-1.067.726-2.373.726-3.918Z',
	},
	{
		key: 'n2',
		d: 'M333.11 8.325V28.69h-3.281l-9.595-13.87h-.169v13.87h-3.689V8.325h3.301l9.585 13.881h.179V8.326h3.669Z',
	},
] as const;

const defaultVariants: Variants = {
	hidden: { opacity: 0, y: 6 },
	visible: (i: number) => ({
		opacity: 1,
		y: 0,
		transition: {
			delay: i * 0.06,
			duration: 0.4,
			ease: [0.16, 1, 0.3, 1],
		},
	}),
};

interface SohanemonsLogoProps extends SVGProps<SVGSVGElement> {
	charVariants?: Variants;
	animate?: boolean;
}

export function SohanemonsLogo({
	charVariants = defaultVariants,
	animate = true,
	...props
}: SohanemonsLogoProps) {
	const maskId = useId();

	return (
		<MotionProvider>
			<svg
				xmlns="http://www.w3.org/2000/svg"
				viewBox="0 0 334 36"
				width={334}
				height={36}
				aria-hidden="true"
				fill="none"
				{...props}
			>
				{/* ── S icon mark ── */}
				<mask
					id={maskId}
					width={37}
					height={36}
					x={0}
					y={0}
					maskUnits="userSpaceOnUse"
					style={{ maskType: 'luminance' }}
				>
					<path fill="#ffffff" d="M0 0h36.25v35.47H0V0Z" />
					<path
						fill="black"
						stroke="black"
						strokeWidth="1.5"
						strokeLinejoin="round"
						d="M13.594 13.302c0-.784.318-1.536.885-2.09a3.055 3.055 0 0 1 2.136-.866h3.02c.625 0 1.235.189 1.745.542.51.353.896.851 1.105 1.428.138.363.417.658.776.821a1.54 1.54 0 0 0 1.142.055 1.5 1.5 0 0 0 .853-.744 1.45 1.45 0 0 0 .077-1.117 5.93 5.93 0 0 0-2.209-2.856 6.136 6.136 0 0 0-3.489-1.085h-3.02a6.11 6.11 0 0 0-4.273 1.731 5.848 5.848 0 0 0-1.77 4.18c0 1.569.637 3.072 1.77 4.18a6.11 6.11 0 0 0 4.272 1.732h3.021c.801 0 1.57.312 2.136.866.567.554.885 1.306.885 2.09 0 .784-.318 1.536-.885 2.09a3.055 3.055 0 0 1-2.136.866h-3.02c-.625 0-1.235-.19-1.745-.542a2.966 2.966 0 0 1-1.105-1.428 1.49 1.49 0 0 0-.776-.822 1.54 1.54 0 0 0-1.142-.054 1.5 1.5 0 0 0-.853.744 1.45 1.45 0 0 0-.077 1.116 5.93 5.93 0 0 0 2.209 2.857 6.136 6.136 0 0 0 3.489 1.085h3.02a6.11 6.11 0 0 0 4.272-1.732 5.848 5.848 0 0 0 1.77-4.18 5.848 5.848 0 0 0-1.77-4.18 6.11 6.11 0 0 0-4.272-1.732h-3.02c-.802 0-1.57-.311-2.136-.865a2.924 2.924 0 0 1-.885-2.09Z"
					/>
				</mask>

				<motion.g
					mask={`url(#${maskId})`}
					variants={charVariants}
					initial={animate ? 'hidden' : false}
					animate={animate ? 'visible' : false}
					custom={0}
				>
					<path fill="currentColor" d="M36.25 0H0v35.47h36.25V0Z" />
				</motion.g>

				{/* ── Text characters ── */}
				{CHARS.map(({ key, ...rest }, i) => (
					<motion.path
						key={key}
						fill="currentColor"
						variants={charVariants}
						initial={animate ? 'hidden' : false}
						animate={animate ? 'visible' : false}
						custom={i + 1}
						{...rest}
					/>
				))}
			</svg>
		</MotionProvider>
	);
}
